import { createPage } from 'client/pages'
import { usePage } from 'client/store/index'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
} from 'react'
import { useTranslation } from 'react-i18next'
import { LibraryElementEnum } from 'src/shared/enums/LibraryElementEnum'
import { getEntityCustomName } from '../utils/get-entity-custom-name'

type GetEntityCustomName = (
  libraryElementType: LibraryElementEnum,
  pageType: PageTypeEnum,
) => string | undefined

type GetEntityCustomNameContextValue = {
  getEntityCustomName: GetEntityCustomName
}

const GetEntityCustomNameContext =
  createContext<GetEntityCustomNameContextValue>({
    getEntityCustomName: () => undefined,
  })

const useGetEntityCustomName = () => {
  const { t } = useTranslation()
  const page = usePage(page => page)

  const getEntityCustomNameCallback = useCallback(
    (libraryElementType: LibraryElementEnum, pageType: PageTypeEnum) => {
      const { createLibraryElement } = createPage(pageType)
      const entity = createLibraryElement(libraryElementType)

      if (!('customName' in entity)) {
        return undefined
      }

      return getEntityCustomName({
        entityType: entity.type,
        entities: page.entities,
        t,
      })
    },
    [page.entities, t],
  )

  return getEntityCustomNameCallback
}

export const useGetEntityCustomNameContext = () =>
  useContext(GetEntityCustomNameContext)

export const GetEntityCustomNameProvider = ({
  children,
}: PropsWithChildren) => {
  const getEntityCustomName = useGetEntityCustomName()
  return (
    <GetEntityCustomNameContext.Provider value={{ getEntityCustomName }}>
      {children}
    </GetEntityCustomNameContext.Provider>
  )
}
